import { MapPin, Mail, Phone, MessageCircle, Calendar, Send } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useInView } from "@/hooks/useInView";

const WHATSAPP_URL = "https://wa.me/351919133855";
const SIGNAL_URL = "https://signal.me/#p/+351919133855";
const BOOK_URL = "https://book.yourholiday.pt/en/search-availability/";

export default function Contact() {
  const { t } = useLanguage();
  const [ref, inView] = useInView();

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#F3F4F1]" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div
          ref={ref}
          className={`text-center transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="h-px w-10 bg-[#3A4D39]/30" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#3A4D39] font-manrope">
              {t("contact.badge")}
            </span>
            <div className="h-px w-10 bg-[#3A4D39]/30" />
          </div>

          {/* Heading */}
          <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-normal text-[#1F2937] leading-tight mb-5">
            {t("contact.title")}
          </h2>
          <p className="text-base md:text-lg font-manrope font-light text-[#6B7280] max-w-xl mx-auto mb-14 leading-relaxed">
            {t("contact.subtitle")}
          </p>

          {/* CTA Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
            {/* WhatsApp */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-1 rounded-2xl px-6 py-5 bg-[#25D366] text-white shadow-lg hover:bg-[#128C7E] hover:-translate-y-1 transition-all duration-300"
            >
              <span className="flex items-center gap-3 text-lg font-semibold font-manrope">
                <MessageCircle className="w-6 h-6 shrink-0" />
                {t("contact.whatsapp")}
              </span>
              <span className="text-xs text-white/80 font-manrope font-light">{t("contact.contactSub")}</span>
            </a>

            {/* Signal */}
            <a
              href={SIGNAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-1 rounded-2xl px-6 py-5 bg-[#3A76F0] text-white shadow-lg hover:bg-[#2C5EBF] hover:-translate-y-1 transition-all duration-300"
            >
              <span className="flex items-center gap-3 text-lg font-semibold font-manrope">
                <Send className="w-6 h-6 shrink-0" />
                {t("contact.signal")}
              </span>
              <span className="text-xs text-white/80 font-manrope font-light">{t("contact.contactSub")}</span>
            </a>

            {/* Availability */}
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-1 rounded-2xl px-6 py-5 bg-white border-2 border-[#D4A373] text-[#1F2937] shadow-md hover:bg-[#D4A373] hover:text-white transition-all duration-300 hover:-translate-y-1"
            >
              <span className="flex items-center gap-3 text-lg font-semibold font-manrope">
                <Calendar className="w-6 h-6 shrink-0" />
                {t("contact.availability")}
              </span>
              <span className="text-xs text-[#9CA3AF] group-hover:text-white/80 font-manrope font-light">
                {t("contact.availabilitySub")}
              </span>
            </a>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-10">
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-xs uppercase tracking-widest text-[#9CA3AF] font-manrope shrink-0">
              {t("contact.infoTitle")}
            </span>
            <div className="flex-1 h-px bg-stone-200" />
          </div>

          {/* Contact info cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col items-center gap-2 bg-white rounded-2xl p-6 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#3A4D39]/10 flex items-center justify-center mb-1">
                <MapPin className="w-5 h-5 text-[#3A4D39]" />
              </div>
              <p className="text-sm font-manrope font-medium text-[#1F2937]">{t("contact.address")}</p>
            </div>

            <div className="flex flex-col items-center gap-2 bg-white rounded-2xl p-6 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#3A4D39]/10 flex items-center justify-center mb-1">
                <Mail className="w-5 h-5 text-[#3A4D39]" />
              </div>
              <a href={`mailto:${t("contact.email")}`} className="text-sm font-manrope font-medium text-[#1F2937] hover:text-[#3A4D39]">
                {t("contact.email")}
              </a>
            </div>

            <div className="flex flex-col items-center gap-2 bg-white rounded-2xl p-6 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#3A4D39]/10 flex items-center justify-center mb-1">
                <Phone className="w-5 h-5 text-[#3A4D39]" />
              </div>
              <p className="text-xs font-manrope text-[#4B5563] text-center leading-relaxed">{t("contact.phones")}</p>
              <p className="text-xs font-manrope text-[#9CA3AF] text-center">{t("contact.office")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
